import { useState } from 'react';
import {
  Box,
  Grid,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  IconButton,
  Snackbar,
  Alert
} from '@mui/material';
import ContentCopy from '@mui/icons-material/ContentCopy';
import { Headset, HeadsetUserUsage } from 'src/models/user';
import { format } from 'date-fns';
import WarrantyEventSection from '../orders/WarrantyEventSection';

type Props = {
  headset: Headset;
};

const UserHeadsetUsageDetails = ({ headset }: Props) => {
  const [toastOpen, setToastOpen] = useState(false);

  let userUsage: HeadsetUserUsage[] = [];
  try {
    userUsage = JSON.parse(headset.userUsage || '{}')?.usage ?? [];
  } catch (e) {
    console.error('Error accessing headset userUsage:', e);
  }

  const handleCopyMac = () => {
    navigator.clipboard.writeText(headset.mac);
    setToastOpen(true);
  };

  const handleCloseToast = () => {
    setToastOpen(false);
  };

  userUsage.sort((a, b) => {
    if (!a.firstSessionDate || !b.firstSessionDate) {
      return 0;
    }
    return b.firstSessionDate!.localeCompare(a.firstSessionDate!);
  });

  return (
    <>
      <Box sx={{ marginTop: '1rem' }}>
        <Grid container spacing={3}>
          <Grid item md={3} xs={12}>
            <Typography color="textPrimary" variant="subtitle2">
              ID
            </Typography>
            <Typography color="textSecondary" variant="body2">
              {headset.id}
            </Typography>
          </Grid>
          <Grid item md={3} xs={12}>
            <Typography color="textPrimary" variant="subtitle2">
              MAC
            </Typography>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Typography color="textSecondary" variant="body2">
                {headset.mac}
              </Typography>
              <IconButton
                size="small"
                onClick={handleCopyMac}
                sx={{ padding: 0.5 }}
              >
                <ContentCopy fontSize="small" />
              </IconButton>
            </Box>
          </Grid>
          <Grid item md={3} xs={12}>
            <Typography color="textPrimary" variant="subtitle2">
              Firmware (Original)
            </Typography>
            <Typography color="textSecondary" variant="body2">
              {headset.firmwareVersion} ({headset.originalFirmwareVersion})
            </Typography>
          </Grid>
          <Grid item md={3} xs={12}>
            <Typography color="textPrimary" variant="subtitle2">
              Hardware & Model
            </Typography>
            <Typography color="textSecondary" variant="body2">
              {headset.hardwareVersion} - {headset.modelNumber}
            </Typography>
          </Grid>
          <Grid item md={3} xs={12}>
            <Typography color="textPrimary" variant="subtitle2">
              First Session
            </Typography>
            <Typography color="textSecondary" variant="body2">
              {format(
                new Date(headset.firstSessionDate.formatted),
                'MMM dd, yyyy'
              )}
            </Typography>
          </Grid>
          <Grid item md={3} xs={12}>
            <Typography color="textPrimary" variant="subtitle2">
              Last Session
            </Typography>
            <Typography color="textSecondary" variant="body2">
              {format(
                new Date(headset.lastSessionDate.formatted),
                'MMM dd, yyyy'
              )}
            </Typography>
          </Grid>
          <Grid item md={3} xs={12}>
            <Typography color="textPrimary" variant="subtitle2">
              Total Session | Assessments
            </Typography>
            <Typography color="textSecondary" variant="body2">
              {headset.totalSessions ?? 0} | {headset.totalAssessments ?? 0}
            </Typography>
          </Grid>
          <Grid item md={3} xs={12}>
            <Typography color="textPrimary" variant="subtitle2">
              No. of Users
            </Typography>
            <Typography color="textSecondary" variant="body2">
              {userUsage.length}
            </Typography>
          </Grid>
          {headset.warrantyEvents.length > 0 && (
            <Grid item xs={12}>
              <Typography color="textPrimary" variant="subtitle2">
                Warranty Events
              </Typography>
              <Box sx={{ display: 'flex' }}>
                <WarrantyEventSection warrantyEvents={headset.warrantyEvents} />
              </Box>
            </Grid>
          )}
          <Grid item xs={12}>
            <Typography color="textPrimary" variant="subtitle2" sx={{ mb: 2 }}>
              User Usage
            </Typography>
            {userUsage.length > 0 ? (
              <TableContainer component={Paper}>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>Email</TableCell>
                      <TableCell align="right">Sessions</TableCell>
                      <TableCell align="right">Assessments</TableCell>
                      <TableCell>First Session</TableCell>
                      <TableCell>Last Session</TableCell>
                      <TableCell>User ID</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {userUsage.map((usage, index) => (
                      <TableRow key={usage.userId || index}>
                        <TableCell>{usage.email ?? 'N/A'}</TableCell>
                        <TableCell align="right">
                          {usage.totalSessions}
                        </TableCell>
                        <TableCell align="right">
                          {usage.totalAssessments}
                        </TableCell>
                        <TableCell>
                          {usage.firstSessionDate
                            ? format(
                                new Date(usage.firstSessionDate),
                                'MMM dd, yyyy'
                              )
                            : 'N/A'}
                        </TableCell>
                        <TableCell>
                          {usage.lastSessionDate
                            ? format(
                                new Date(usage.lastSessionDate),
                                'MMM dd, yyyy'
                              )
                            : 'N/A'}
                        </TableCell>
                        <TableCell>{usage.userId}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            ) : (
              <Typography color="textSecondary" variant="body2">
                No user usage data available
              </Typography>
            )}
          </Grid>
        </Grid>
      </Box>
      <Snackbar
        open={toastOpen}
        autoHideDuration={2000}
        onClose={handleCloseToast}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert
          onClose={handleCloseToast}
          severity="success"
          sx={{ width: '100%' }}
        >
          Copied to clipboard!
        </Alert>
      </Snackbar>
    </>
  );
};

export default UserHeadsetUsageDetails;
